import { Fingerprint, Nfc, QrCode, ScanFace } from 'lucide-react';

export const CHECKIN_METHODS = [
  {
    slug: 'qr-code',
    icon: QrCode,
    title: 'QR codes',
    card: 'On every confirmation email, badge and the attendee portal.',
    headline: 'QR code check-in that keeps the gate moving',
    intro: 'Every confirmed attendee gets a unique QR code. Staff scan it from any phone or a USB scanner and the attendee is checked in within a second.',
    steps: [
      ['Attendee registers', 'The QR code is issued as soon as the registration is confirmed, after payment or approval where those apply.'],
      ['Code reaches the attendee', 'It is in the confirmation email, on the printed badge and in the attendee portal, so a lost email is never a problem.'],
      ['Staff scan at the gate', 'Use the camera on any phone or tablet, or plug in a USB/Bluetooth scanner at a desk for the fastest throughput.'],
      ['Result in a second', 'The screen shows the name, category and photo. Duplicates, blocked categories and cancelled registrations are flagged straight away.'],
    ],
    features: [
      'Separate scan purposes for entry, lunch, kits, halls or workshops, each with its own count',
      'Repeat scans flagged instantly, with details of the earlier scan',
      'Works offline at the venue and syncs when the network returns',
      'Live counts and scan history for your committee',
    ],
    needs: ['Any smartphone or tablet with a camera', 'Optional: USB or Bluetooth 2D barcode scanner'],
    bestFor: 'Conferences, exhibitions and any event where most attendees register in advance.',
  },
  {
    slug: 'face-recognition',
    icon: ScanFace,
    title: 'Face recognition',
    card: 'Optional selfie at registration for contactless entry.',
    headline: 'Contactless entry with face recognition',
    intro: 'Attendees add a photo when they register. At the gate the camera matches their face to the registration, so nobody has to find a QR code.',
    steps: [
      ['Turn it on for the event', 'Enable face check-in in the event settings. Events that do not need it are unaffected.'],
      ['Attendee adds a photo', 'A selfie or uploaded photo is captured on the registration form, with a quick crop so faces are framed well.'],
      ['Camera at the gate', 'Staff pick Face recognition on the scanner screen and point a phone or tablet camera at the attendee.'],
      ['Matched and checked in', 'The face is matched to the registration and the attendee is checked in. If there is no match, staff switch to QR or search on the same screen.'],
    ],
    features: [
      'No phone or printout needed for the attendee',
      'The registration photo is shown to staff, which also helps verify QR and manual check-ins',
      'Same scan purposes, counts and duplicate checks as QR',
      'Can be switched on per event',
    ],
    needs: ['Phone or tablet with a front or rear camera', 'Reasonable lighting at the entry point', 'Internet connection at the gate for face matching'],
    bestFor: 'VIP and invite-only events, repeat entry over multi-day events, and gates where speed matters more than queues for printouts.',
  },
  {
    slug: 'rfid',
    icon: Nfc,
    title: 'RFID cards',
    card: 'Tap-to-enter with cards assigned at the desk.',
    headline: 'Tap-to-enter with RFID cards',
    intro: 'Hand out RFID cards or wristbands at the registration desk. Each card is linked to one attendee, who then just taps it at every gate.',
    steps: [
      ['Attendee arrives at the desk', 'Find the registration by QR code or search, as usual.'],
      ['Assign a card', 'Tap the card on the desk reader to link it to that registration. One card can cover several linked events.'],
      ['Tap at the gate', 'The reader at each entry sends the card number to the scanner screen, which checks the attendee in.'],
      ['Same rules as QR', 'Scan purposes, duplicate checks and blocked categories apply exactly as they do for QR scans.'],
    ],
    features: [
      'Fastest entry for repeat access across halls and days',
      'Works with standard USB RFID readers, with no driver or app needed',
      'Cards can be reassigned if lost',
      'Counts and history roll up with QR and face scans',
    ],
    needs: ['USB RFID reader that types the card number (keyboard mode)', 'RFID cards or wristbands', 'A laptop or tablet at each reader'],
    bestFor: 'Multi-day conferences, sessions with hall-level tracking, and events that re-admit attendees many times.',
  },
  {
    slug: 'search-manual',
    icon: Fingerprint,
    title: 'Search & manual',
    card: 'Name, phone or ID lookup when someone forgets their QR.',
    headline: 'Search and manual check-in for everyone else',
    intro: 'When someone forgets their QR code or has no phone, staff find them by name, phone, email or registration ID and check them in from the same screen.',
    steps: [
      ['Type to search', 'Start typing a name, phone number, email or registration ID in the scanner search box.'],
      ['Confirm the person', 'Matching registrations appear with name, category and photo, so staff pick the right one.'],
      ['Check in or register on the spot', 'Check them in with one click. Walk-ins can be registered at the kiosk desk, singly or as a group.'],
      ['Print the badge', 'The badge prints the moment the attendee is found, on thermal or A4 printers, or via AirPrint from an iPad.'],
    ],
    features: [
      'Search by name, phone, email or registration ID',
      'On-spot and group registration at the kiosk desk',
      'Instant badge printing',
      'Every manual check-in is logged with the staff member and time',
    ],
    needs: ['Any laptop, tablet or phone', 'Optional: badge printer at the desk'],
    bestFor: 'Help desks, walk-ins and a fallback lane at every event.',
  },
];

export const checkInMethod = (slug) => CHECKIN_METHODS.find((m) => m.slug === slug);
